// Switch-model access helpers for the tenant payload (/public/tenant).
//
// Three layers decide what a fan or tenant admin can reach:
//   1. Account status (super-admin): suspended → closed site; not live →
//      paused site. Never applied to /admin paths here.
//   2. Six add-ons (super-admin): scorebook, broadcast, fan_ai, ticketing,
//      white_label, registration (in no package). 1.0's Forms add-on was
//      retired at registration step R: a stale payload listing 'forms' is
//      harmless because no gate asks for it.
//      Everything else is core and always on.
//   3. Site sections (tenant admin, per sport): which public pages exist.
//
// Plain module (no components) so LeagueContext.tsx keeps exporting only
// React components/hooks for fast refresh. Everything here is pure.

import type { TenantInfo } from './LeagueContext'

// Tenant-controlled site sections. One map per sport, resolved by the backend
// for the sport applied to the payload. Turning a section off hides its
// nav/footer link AND redirects its route to "/".
export type SiteSectionKey = 'schedule' | 'players' | 'teams' | 'stats' | 'gallery' | 'blog' | 'venues'
export type TenantSiteSections = Record<SiteSectionKey, boolean>

export const SITE_SECTION_KEYS: SiteSectionKey[] = [
  'schedule', 'players', 'teams', 'stats', 'gallery', 'blog', 'venues',
]

// Defaults when the payload says nothing: every section on except venues
// (the venues page has always been opt-in).
export const DEFAULT_SITE_SECTIONS: TenantSiteSections = {
  schedule: true,
  players: true,
  teams: true,
  stats: true,
  gallery: true,
  blog: true,
  venues: false,
}

// The six add-ons. Everything else is core (always on).
// 1.0 launch (P2): live_scoring, sponsors and analytics joined them (Pro and up).
export type AddonKey = 'scorebook' | 'broadcast' | 'fan_ai' | 'ticketing' | 'white_label' | 'registration' | 'live_scoring' | 'sponsors' | 'analytics'
export const ADDON_KEYS: AddonKey[] = ['scorebook', 'broadcast', 'fan_ai', 'ticketing', 'white_label', 'registration', 'live_scoring', 'sponsors', 'analytics']

// Add-ons checked STRICTLY: on only when the payload lists them, off when the
// tenant is missing, still loading or an older payload says nothing. The
// registration plan (section 15) forbids the fail-open fallback below for
// registration: nothing about it may appear for a league that is not
// switched on. The site route table's `onlyWith` gate takes only these keys.
export type StrictAddonKey = 'registration'
const STRICT_ADDONS: readonly StrictAddonKey[] = ['registration']

// How registration shows on the public site (Website › Design ›
// Registration): the Register link in the menu, the home page spotlight,
// and upcoming registrations in the Register list and the spotlight. Each is
// on unless the payload says false (an older payload says nothing).
export type RegistrationSiteKey = 'menu_tab' | 'home_spotlight' | 'show_upcoming'
export const REGISTRATION_SITE_KEYS: RegistrationSiteKey[] = ['menu_tab', 'home_spotlight', 'show_upcoming']

export function registrationSiteOn(tenant: TenantInfo | null, key: RegistrationSiteKey): boolean {
  return tenant?.registration_site?.[key] !== false
}

export function isSectionKey(key: string): key is SiteSectionKey {
  return (SITE_SECTION_KEYS as string[]).includes(key)
}

// Whether a public site section is visible. Resolution order:
//   1. site_sections[key] from the payload (the switch-model source of truth)
//   2. legacy fields on older payloads / cached tenants (only when the
//      payload has no site_sections at all): features[key], or
//      venues_page_enabled for venues
//   3. DEFAULT_SITE_SECTIONS (all on except venues)
export function sectionEnabled(tenant: TenantInfo | null, key: SiteSectionKey): boolean {
  const fromSections = tenant?.site_sections?.[key]
  if (typeof fromSections === 'boolean') return fromSections
  if (tenant && !tenant.site_sections) {
    if (key === 'venues') {
      if (typeof tenant.venues_page_enabled === 'boolean') return tenant.venues_page_enabled
    } else {
      const legacy = tenant.features?.[key]
      if (typeof legacy === 'boolean') return legacy
    }
  }
  return DEFAULT_SITE_SECTIONS[key]
}

// Old gateable keys an add-on absorbed. A pre-switch-model payload (a tenant
// cached in localStorage, or an older / rolled-back backend) lists these in
// `entitlements` and never the add-on key itself.
const LEGACY_ADDON_ALIASES: Partial<Record<AddonKey, string[]>> = {
  broadcast: ['score_bugs', 'score_bug_templates', 'live_events', 'commentator'],
  white_label: ['share_cards', 'custom_domain'],
}

// Whether one of the six add-ons is entitled. Prefers the dedicated
// `addons` list, then `entitlements` (including the legacy keys the add-on
// absorbed); fails OPEN when neither is present (older payload or still
// loading) — the backend is the real gate. STRICT_ADDONS fail CLOSED
// instead (plan section 15).
export function addonEntitled(tenant: TenantInfo | null, key: AddonKey): boolean {
  if ((STRICT_ADDONS as readonly AddonKey[]).includes(key)) {
    return Array.isArray(tenant?.addons)
      ? tenant!.addons!.includes(key)
      : Array.isArray(tenant?.entitlements)
        ? tenant!.entitlements!.includes(key)
        : false
  }
  if (!tenant) return true
  if (Array.isArray(tenant.addons)) return tenant.addons.includes(key)
  if (Array.isArray(tenant.entitlements)) {
    const ent = tenant.entitlements
    return ent.includes(key) || (LEGACY_ADDON_ALIASES[key] ?? []).some((k) => ent.includes(k))
  }
  return true
}

// Suspended by ScoreLM (account status), or the legacy disabled flag, which
// the backend keeps in sync with status 'suspended' for one release.
export function isTenantSuspended(tenant: TenantInfo | null): boolean {
  if (!tenant) return false
  return tenant.is_active === false || tenant.subscription?.status === 'suspended'
}

// A members-only league (registration steps 32-33): the public site renders
// the sign-in wall for a visitor who is not admitted, and the public client
// sends its Bearer. A missing `visibility` (an older cached tenant) means
// public; the server's members-only gate is the control. Not a publicGateFor
// state: the wall lives inside SiteTemplateHost, so /admin, the open member
// pages and the standalone pages are never walled (T7).
export function isMembersOnly(tenant: TenantInfo | null | undefined): boolean {
  return tenant?.visibility === 'members_only'
}

// #1386: kept out of search engines: a trial league (trialing, or a trial
// that ran out), and any league whose site shows a placeholder (ClosedPage or
// SubscriptionInactivePage, i.e. publicGateFor not null), so a placeholder
// never replaces its real pages in search results, and a members-only league
// (its signed-out visitors see only the sign-in wall). A live public league
// and an older payload with no subscription stay indexable. Same rule as the
// backend's noindexFor plus isMembersOnly (services/indexing.ts), which drive
// X-Robots-Tag and /public/indexing.
export function tenantNoindex(tenant: TenantInfo | null): boolean {
  if (!tenant) return false
  if (isMembersOnly(tenant)) return true
  const s = tenant.subscription
  if (s && (s.status === 'trialing' || (s.status === 'expired' && s.trial_ends_at != null))) return true
  return publicGateFor(tenant, '/') !== null
}

export type PublicGate = 'closed' | 'inactive' | null

// Which full-screen page replaces the public site, if any.
//   - 'closed'   — suspended (status 'suspended', or the legacy
//                  is_active=false the backend keeps in sync) → ClosedPage
//   - 'inactive' — subscription not live (lapsed trial, canceled, …)
//                  → SubscriptionInactivePage
// Admin paths are never gated here: a not-live league can still log in and
// pay, and a suspended league's admin answers 503 from the API itself.
export function publicGateFor(league: TenantInfo | null, pathname: string): PublicGate {
  if (!league) return null
  if (pathname.startsWith('/admin')) return null
  if (isTenantSuspended(league)) return 'closed'
  if (league.subscription?.is_live === false) return 'inactive'
  return null
}
